import React, { useState, useEffect, useRef } from 'react';
import { Video, Store, RefreshCw, RotateCw, CheckCircle } from 'lucide-react';
import authService from '../services/AuthService';
import { API_BASE } from '../consts';

// ── Module-level shared state — one fetch, one poll for ALL badge instances ──
let cache = null;
let fetchPromise = null;
let pollInterval = null;
const subscribers = new Set(); // all mounted badge instances listen here

const notifyAll = (newCache) => {
    cache = newCache;
    subscribers.forEach(fn => fn(newCache));
};

const bustCache = () => {
    cache = null;
    fetchPromise = null;
};

const loadCache = async () => {
    if (cache) return cache;
    if (fetchPromise) return fetchPromise;

    fetchPromise = Promise.all([
        authService.authenticatedFetch(API_BASE + '/api/aip/my-storefront').then(r => r.json()),
        authService.authenticatedFetch(API_BASE + '/api/aip/my-asin-counts').then(r => r.json()),
    ]).then(([sf, counts]) => {
        const fresh = {
            storefront_url: sf.storefront_url || '',
            sync_status: sf.sync_status || 'idle',
            last_synced_at: sf.last_synced_at,
            counts: counts.asin_counts || {},
        };
        fetchPromise = null;
        notifyAll(fresh);
        return fresh;
    }).catch(() => {
        const empty = { storefront_url: '', sync_status: 'idle', last_synced_at: null, counts: {} };
        fetchPromise = null;
        notifyAll(empty);
        return empty;
    });

    return fetchPromise;
};

// One shared poll — checks every 10s, stops itself when done
const startSharedPolling = () => {
    if (pollInterval) return;
    console.log('[AsinVideoBadge] Starting poll every 30s');
    pollInterval = setInterval(async () => {
        try {
            const res = await authService.authenticatedFetch(API_BASE + '/api/aip/my-storefront');
            const sf = await res.json();
            console.log('[AsinVideoBadge] Poll result:', sf.sync_status);
            const stillSyncing = sf.sync_status === 'syncing' || sf.sync_status === 'pending';

            if (!stillSyncing) {
                console.log('[AsinVideoBadge] Sync done, reloading counts');
                stopSharedPolling();
                bustCache();
                await loadCache();
            } else {
                if (cache) {
                    notifyAll({ ...cache, sync_status: sf.sync_status });
                }
            }
        } catch (e) {
            console.error('Poll error', e);
        }
    }, 30000);
};

const stopSharedPolling = () => {
    if (pollInterval) { clearInterval(pollInterval); pollInterval = null; }
};

// ── Tooltip ──
const Tooltip = ({ text, children }) => (
    <div className="relative group inline-flex">
        {children}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block z-50 pointer-events-none">
            <div className="bg-gray-900 text-white text-[10px] rounded px-2 py-1 whitespace-nowrap shadow-lg">
                {text}
            </div>
        </div>
    </div>
);

// ── Component ──
const AsinVideoBadge = ({ asin, user }) => {
    const [data, setData] = useState(cache); // start from cache if already loaded
    const [showInput, setShowInput] = useState(false);
    const [inputUrl, setInputUrl] = useState('');
    const [saving, setSaving] = useState(false);
    const [justSaved, setJustSaved] = useState(false);

    // Subscribe to shared cache updates
    useEffect(() => {
        if (!user || !asin) return;

        const onUpdate = (newCache) => {
            setData(newCache);
            setInputUrl(newCache.storefront_url);
        };

        subscribers.add(onUpdate);

        // Load if not cached yet
        if (!cache) {
            loadCache();
        } else {
            // Already cached — init local state
            setData(cache);
            setInputUrl(cache.storefront_url);
            // If was already syncing when component mounted, ensure poll is running
            if (cache.sync_status === 'syncing' || cache.sync_status === 'pending') {
                startSharedPolling();
            }
        }

        return () => {
            subscribers.delete(onUpdate);
            // Stop polling if no one is listening anymore
            if (subscribers.size === 0) stopSharedPolling();
        };
    }, [user, asin]);

    // Show input if no storefront set yet
    useEffect(() => {
        if (data && !data.storefront_url) setShowInput(true);
    }, [data?.storefront_url]);

    const handleSave = async () => {
        const url = inputUrl.trim();
        if (!url) return;
        if (!url.includes('/shop/')) {
            alert('URL must contain /shop/ (e.g. amazon.com/shop/yourname)');
            return;
        }
        setSaving(true);
        try {
            const res = await authService.authenticatedFetch(API_BASE + '/api/aip/my-storefront', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ storefront_url: url }),
            });
            if (res.ok) {
                setShowInput(false);
                setJustSaved(true);
                setTimeout(() => setJustSaved(false), 3000);
                // Trigger sync immediately
                await authService.authenticatedFetch(API_BASE + '/api/aip/my-storefront/sync', {
                    method: 'POST',
                });
                bustCache();
                // Update cache optimistically so all badges reflect new URL + syncing
                notifyAll({
                    storefront_url: url,
                    sync_status: 'pending',
                    last_synced_at: cache?.last_synced_at || null,
                    counts: cache?.counts || {},
                });
                startSharedPolling();
            }
        } catch { alert('Failed to save'); }
        finally { setSaving(false); }
    };

    const handleSync = async () => {
        if (!data?.storefront_url) return;
        const isSyncing = data.sync_status === 'syncing' || data.sync_status === 'pending';
        if (isSyncing) return;

        try {
            await authService.authenticatedFetch(API_BASE + '/api/aip/my-storefront/sync', {
                method: 'POST',
            });
            notifyAll({ ...cache, sync_status: 'pending' });
            startSharedPolling();
        } catch (e) {
            console.error('Sync trigger failed', e);
        }
    };

    if (!user || !asin) return null;
    if (!data) return null; // first load not done yet

    const count = data.counts[asin] ?? 0;
    const isSyncing = data.sync_status === 'syncing' || data.sync_status === 'pending';

    // ── No storefront or editing ──
    if (!data.storefront_url || showInput) {
        return (
            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                <Store size={9} className="text-gray-400 flex-shrink-0" />
                <input
                    type="text"
                    value={inputUrl}
                    onChange={e => setInputUrl(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSave()}
                    placeholder="amazon.com/shop/you"
                    className="text-[10px] border border-gray-300 rounded px-1.5 py-0.5 w-36 focus:outline-none focus:ring-1 focus:ring-teal-400"
                />
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="text-[10px] font-semibold text-white bg-teal-600 hover:bg-teal-700 px-2 py-0.5 rounded transition disabled:opacity-50 flex items-center gap-0.5"
                >
                    {saving
                        ? <><RefreshCw size={9} className="animate-spin" /> Saving…</>
                        : 'Set'
                    }
                </button>
                {data.storefront_url && (
                    <button
                        onClick={() => setShowInput(false)}
                        className="text-[10px] text-gray-400 hover:text-gray-600"
                    >✕</button>
                )}
            </div>
        );
    }

    // ── Just saved confirmation ──
    if (justSaved) {
        return (
            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-teal-600">
                    <CheckCircle size={9} /> Saved! Syncing your storefront…
                </span>
                <RotateCw size={9} className="text-teal-400 animate-spin" />
            </div>
        );
    }

    // ── Storefront set — badge + sync button ──
    return (
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            <button
                onClick={() => setShowInput(true)}
                title="Click to change storefront URL"
                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border transition ${count > 0
                        ? 'text-teal-700 bg-teal-50 border-teal-200 hover:bg-teal-100'
                        : 'text-gray-400 bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
            >
                <Video size={9} />
                {count > 0 ? `${count} video${count > 1 ? 's' : ''}` : 'No videos'}
            </button>

            {isSyncing ? (
                <Tooltip text="Your storefront data is updating and will be fully synced soon">
                    <span className="inline-flex items-center p-0.5 text-gray-300 cursor-not-allowed">
                        <RotateCw size={10} className="animate-spin" />
                    </span>
                </Tooltip>
            ) : (
                <Tooltip text={data.last_synced_at
                    ? `Last synced: ${new Date(data.last_synced_at).toLocaleString()}. Click to sync now.`
                    : 'Click to sync now'
                }>
                    <button
                        onClick={handleSync}
                        className="inline-flex items-center p-0.5 text-gray-400 hover:text-teal-600 transition"
                    >
                        <RotateCw size={10} />
                    </button>
                </Tooltip>
            )}
        </div>
    );
};

export default AsinVideoBadge;